if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.autoplayVideos = this.querySelectorAll('[data-autoplay-video]');
        this.setupAutoplayVideos();
        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.viewer.slider?.addEventListener('scroll', debounce(this.onViewerScroll.bind(this), 120));
        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const activeItem = event.detail.currentElement;
        if (!activeItem) return;

        const thumbnail = this.elements.thumbnails?.querySelector(
          `[data-target="${activeItem.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        this.playActiveMedia(activeItem);
      }

      onViewerScroll() {
        if (this.mql.matches || !this.elements.viewer?.slider) return;

        const sliderRect = this.elements.viewer.slider.getBoundingClientRect();
        let activeItem;
        let largestVisibleWidth = 0;

        this.elements.viewer.sliderItems.forEach((item) => {
          const itemRect = item.getBoundingClientRect();
          const visibleWidth = Math.max(0, Math.min(itemRect.right, sliderRect.right) - Math.max(itemRect.left, sliderRect.left));
          if (visibleWidth > largestVisibleWidth) {
            activeItem = item;
            largestVisibleWidth = visibleWidth;
          }
        });

        if (!activeItem) return;

        this.elements.viewer.sliderItems.forEach((item) => item.classList.toggle('is-active', item === activeItem));
        this.playActiveMedia(activeItem);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        this.pauseAutoplayVideosExcept(activeItem);
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (!deferredMedia) return;

        deferredMedia.loadContent(false);
        if (deferredMedia.hasAttribute('data-autoplay-video')) this.playAutoplayVideo(deferredMedia);
      }

      setupAutoplayVideos() {
        if (!this.autoplayVideos.length) return;

        if (!('IntersectionObserver' in window)) {
          const activeItem = this.elements.viewer?.querySelector('.product__media-item.is-active');
          if (activeItem) this.playActiveMedia(activeItem);
          return;
        }

        this.autoplayObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && entry.intersectionRatio >= 0.65) {
                this.playAutoplayVideo(entry.target);
              } else {
                this.pauseAutoplayVideo(entry.target);
              }
            });
          },
          { threshold: [0, 0.65] }
        );

        this.autoplayVideos.forEach((video) => this.autoplayObserver.observe(video));
      }

      playAutoplayVideo(deferredMedia) {
        this.pauseAutoplayVideosExcept(deferredMedia);
        deferredMedia.loadContent(false);

        const video = deferredMedia.querySelector('video');
        if (!video || document.hidden) return;

        video.muted = true;
        const playPromise = video.play();
        if (playPromise) playPromise.catch(() => {});
      }

      pauseAutoplayVideo(deferredMedia) {
        const video = deferredMedia.querySelector('video');
        if (video) {
          video.pause();
          return;
        }

        const externalVideo = deferredMedia.querySelector('.js-youtube, .js-vimeo');
        if (!externalVideo?.contentWindow) return;

        if (externalVideo.classList.contains('js-youtube')) {
          externalVideo.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
        } else {
          externalVideo.contentWindow.postMessage('{"method":"pause"}', '*');
        }
      }

      pauseAutoplayVideosExcept(activeElement) {
        this.autoplayVideos.forEach((deferredMedia) => {
          if (!activeElement.contains(deferredMedia) && deferredMedia !== activeElement) {
            this.pauseAutoplayVideo(deferredMedia);
          }
        });
      }

      disconnectedCallback() {
        this.autoplayObserver?.disconnect();
        this.autoplayVideos?.forEach((deferredMedia) => this.pauseAutoplayVideo(deferredMedia));
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
